import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError, API_BASE } from "../lib/api";
import { fmtCLP } from "../lib/format";
import { toast } from "sonner";
import { FileSpreadsheet, FileText } from "lucide-react";

function firstOfYear() {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function EstadoResultados() {
  const [desde, setDesde] = useState(firstOfYear());
  const [hasta, setHasta] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reportes/estado-resultados-estructurado", { params: { desde, hasta } });
      setData(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [desde, hasta]);

  const downloadFile = (format) => {
    const params = new URLSearchParams({ desde, hasta });
    window.open(`${API_BASE}/reportes/estado-resultados/${format}?${params}`, "_blank");
  };

  const Row = ({ label, value, type = "normal", indent = 0 }) => {
    const styles = {
      normal: "text-foreground",
      subtotal: "font-semibold bg-muted text-foreground",
      total: "font-semibold bg-success/15 text-primary text-base",
      sign: "text-muted-foreground italic",
    };
    return (
      <tr className={styles[type]}>
        <td style={{ paddingLeft: 12 + indent * 16 }}>{label}</td>
        <td className="num">{fmtCLP(value)}</td>
      </tr>
    );
  };

  return (
    <Page>
      <PageHeader
        title="Estado de resultados"
        subtitle="Pérdidas y ganancias del período"
        testid="estado-resultados-header"
        actions={
          <div className="flex items-center gap-2">
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="er-desde"
              className="px-2 py-1.5 border border-border rounded-sm text-sm" />
            <span className="text-muted-foreground text-xs">→</span>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="er-hasta"
              className="px-2 py-1.5 border border-border rounded-sm text-sm" />
            <button onClick={() => downloadFile("excel")} data-testid="er-excel"
              className="inline-flex items-center gap-1.5 border border-success text-success hover:bg-success/10 text-sm px-3 py-1.5 rounded-sm">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>
            <button onClick={() => downloadFile("pdf")} data-testid="er-pdf"
              className="inline-flex items-center gap-1.5 border border-destructive text-destructive hover:bg-destructive/10 text-sm px-3 py-1.5 rounded-sm">
              <FileText className="h-4 w-4" /> PDF
            </button>
          </div>
        }
      />

      {loading || !data ? (
        <div className="text-muted-foreground text-sm">Cargando…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-card border border-border rounded-md overflow-x-auto">
            <table className="acc-table" data-testid="er-table">
              <thead>
                <tr>
                  <th>Concepto</th>
                  <th className="num" style={{ width: 180 }}>Monto (CLP)</th>
                </tr>
              </thead>
              <tbody>
                <Row label="Ventas brutas" value={data.ventas_brutas.total} />
                <Row label="(−) Devoluciones y descuentos" value={-data.devoluciones.total} type="sign" />
                <Row label="= Ventas netas" value={data.ventas_netas} type="subtotal" />
                <Row label="(−) Costo de ventas" value={-data.costo_ventas.total} type="sign" />
                <Row label="= Utilidad bruta" value={data.utilidad_bruta} type="subtotal" />
                <Row label="(−) Gastos administrativos" value={-data.gastos_admin.total} type="sign" />
                <Row label="(−) Gastos de ventas" value={-data.gastos_ventas.total} type="sign" />
                <Row label="= Gastos operativos totales" value={-data.gastos_operativos} type="subtotal" />
                <Row label="= Utilidad operativa" value={data.utilidad_operativa} type="subtotal" />
                <Row label="(+) Ingresos financieros" value={data.ingresos_financieros.total} type="sign" />
                <Row label="(−) Gastos financieros" value={-data.gastos_financieros.total} type="sign" />
                <Row label="= Resultado financiero neto" value={data.resultado_financiero} type="subtotal" />
                {(data.otros_ingresos.total !== 0 || data.otros_egresos.total !== 0) && (
                  <>
                    <Row label="(+) Otros ingresos" value={data.otros_ingresos.total} type="sign" />
                    <Row label="(−) Otros egresos" value={-data.otros_egresos.total} type="sign" />
                  </>
                )}
                <Row label="= Utilidad antes de impuestos" value={data.utilidad_antes_impuestos} type="subtotal" />
                <Row label="(−) Impuestos" value={-data.impuestos.total} type="sign" />
                <Row label="= UTILIDAD NETA" value={data.utilidad_neta} type="total" />
              </tbody>
            </table>
          </div>

          <div className="bg-card border border-border rounded-md p-5 h-fit space-y-4">
            <div>
              <div className="label-caps">Período</div>
              <div className="font-display text-lg font-medium mt-1">{desde} → {hasta}</div>
            </div>
            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Ventas netas</span>
                <span className="num font-semibold">{fmtCLP(data.ventas_netas)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Utilidad bruta</span>
                <span className="num font-semibold text-success">{fmtCLP(data.utilidad_bruta)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Utilidad operativa</span>
                <span className="num font-semibold text-success">{fmtCLP(data.utilidad_operativa)}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-border">
                <span className="label-caps">Utilidad neta</span>
                <span className={`num text-2xl font-semibold ${data.utilidad_neta >= 0 ? "text-primary" : "text-destructive"}`}>
                  {fmtCLP(data.utilidad_neta)}
                </span>
              </div>
              {data.ventas_netas > 0 && (
                <>
                  <div className="pt-3 border-t border-border">
                    <div className="label-caps mb-1">Margen bruto</div>
                    <div className="num text-lg">{((data.utilidad_bruta / data.ventas_netas) * 100).toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="label-caps mb-1">Margen neto</div>
                    <div className="num text-lg">{((data.utilidad_neta / data.ventas_netas) * 100).toFixed(1)}%</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
